import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import {
  areaClusters,
  areaFarms,
  areaScopeKinds,
  defaultScopeId,
  scopesForManager,
  validateAccessDraft,
  type ManageArea,
  type ScopeKind,
} from '@/pages/team/teamLogic';
import { useInviteUser } from '@/pages/team/hooks/useTeamQueries';
import type { ClusterOption, FarmOption } from '@/pages/team/types';
import { RoleSelect } from './RoleSelect';
import { ScopeSelector } from './ScopeSelector';

interface InviteDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clusters: ClusterOption[];
  farms: FarmOption[];
  /** Клик «посмотреть приглашение» при 409 — прокручивает к pending-секции. */
  onViewExisting?: (email: string) => void;
  /** AK-886: роли, которые можно выдать (`grantableRoles(can_invite_codes)`). */
  roles: readonly string[];
  /** AK-886: область управляющего из `GET /users`; нет — без ограничений. */
  area?: ManageArea | null;
  /** Хозяйство из переключателя — подставляется в зону «Хозяйство». */
  sessionFarmId?: string | null;
}

/** Роль по умолчанию: агроном (самое частое приглашение), иначе первая доступная. */
function defaultRole(roles: readonly string[]): string {
  return roles.includes('agronom') ? 'agronom' : (roles[0] ?? '');
}

export function InviteDrawer({
  open,
  onOpenChange,
  clusters,
  farms,
  onViewExisting,
  roles,
  area,
  sessionFarmId,
}: InviteDrawerProps) {
  const invite = useInviteUser();
  const visibleScopes = areaScopeKinds(area);
  const scopeClusters = areaClusters(clusters, area);
  const scopeFarms = areaFarms(farms, area);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(() => defaultRole(roles));
  const [scope, setScope] = useState<ScopeKind>('cluster');
  const [scopeId, setScopeId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [scopeError, setScopeError] = useState<string | undefined>();
  const [conflict, setConflict] = useState<{ kind: 'invited' | 'member'; email: string } | null>(
    null,
  );

  // Reset on open. Зона — первая допустимая для роли в области управляющего.
  useEffect(() => {
    if (open) {
      const r = defaultRole(roles);
      setEmail('');
      setRole(r);
      setScope(scopesForManager(r, area)[0] ?? 'cluster');
      setScopeId(null);
      setMessage('');
      setEmailError(undefined);
      setScopeError(undefined);
      setConflict(null);
    }
    // roles/area читаются на открытии; смена ответа GET /users посреди ввода форму не сбрасывает.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // При смене роли — если текущий scope несовместим (с ролью или областью
  // управляющего), откатываем на первый допустимый.
  useEffect(() => {
    const allowed = scopesForManager(role, area);
    if (allowed.length > 0 && !allowed.includes(scope)) {
      setScope(allowed[0]);
      setScopeId(null);
      setScopeError(undefined);
    }
  }, [role, scope, area]);

  // AK-886: хозяйство подставляется само — текущее из переключателя или
  // единственное доступное (у ролей хозяйства зона «Хозяйство» обязательна).
  useEffect(() => {
    if (!open || scopeId) return;
    const preset =
      scope === 'farm'
        ? defaultScopeId(scopeFarms, sessionFarmId)
        : scope === 'cluster'
          ? defaultScopeId(scopeClusters, null)
          : null;
    if (preset) setScopeId(preset);
  }, [open, scope, scopeId, scopeFarms, scopeClusters, sessionFarmId]);

  const submit = async () => {
    setConflict(null);
    const v = validateAccessDraft({ email, role, scope, scopeId }, true);
    setEmailError(v.emailError);
    setScopeError(v.scopeError);
    if (!v.ok) return;

    try {
      await invite.mutateAsync({
        email: email.trim(),
        role,
        scope,
        scope_id: scope === 'company' ? null : scopeId,
        personal_message: message.trim() || null,
      });
      onOpenChange(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const isMember = /состоит/i.test(e.detail);
        setConflict({ kind: isMember ? 'member' : 'invited', email: email.trim() });
        setEmailError(
          isMember
            ? 'Этот пользователь уже в команде'
            : 'Этот email уже приглашён',
        );
      } else if (e instanceof ApiError) {
        setScopeError(e.detail);
      } else {
        setScopeError('Не удалось отправить приглашение');
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[480px] overflow-y-auto">
        <SheetTitle>Пригласить в команду</SheetTitle>
        <SheetDescription>
          Отправим ссылку на почту. По ней человек задаст пароль и войдёт.
        </SheetDescription>

        <div className="mt-6 space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="invite-email"
              type="email"
              inputMode="email"
              autoComplete="off"
              placeholder="name@company.ru"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError(undefined);
                setConflict(null);
              }}
              aria-invalid={!!emailError}
            />
            {emailError && (
              <p className="text-xs text-destructive">
                {emailError}
                {conflict?.kind === 'invited' && onViewExisting && (
                  <>
                    {' — '}
                    <button
                      type="button"
                      className="underline underline-offset-2"
                      onClick={() => {
                        onOpenChange(false);
                        onViewExisting(conflict.email);
                      }}
                    >
                      посмотреть приглашение
                    </button>
                  </>
                )}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>
              Роль <span className="text-destructive">*</span>
            </Label>
            <RoleSelect value={role} onChange={setRole} roles={roles} />
          </div>

          <div className="space-y-1.5">
            <Label>Зона доступа</Label>
            <ScopeSelector
              role={role}
              scope={scope}
              scopeId={scopeId}
              clusters={scopeClusters}
              farms={scopeFarms}
              visibleScopes={visibleScopes}
              onScopeChange={(s) => {
                setScope(s);
                setScopeId(null);
                setScopeError(undefined);
              }}
              onScopeIdChange={(id) => {
                setScopeId(id);
                setScopeError(undefined);
              }}
              error={scopeError}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invite-msg">Личное сообщение · необязательно</Label>
            <textarea
              id="invite-msg"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Привет! Приглашаю тебя в команду…"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>
        </div>

        <div className="mt-6 flex gap-3 border-t border-border pt-4">
          <Button
            variant="ghost"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={invite.isPending}
          >
            Отмена
          </Button>
          <Button
            className="flex-1"
            onClick={submit}
            disabled={invite.isPending}
          >
            {invite.isPending ? 'Отправляем…' : 'Отправить приглашение'}
          </Button>
        </div>
        <p className="mt-3 text-center text-xs text-muted-foreground">
          Ссылка активна 7 дней. Потом можно выслать заново.
        </p>
      </SheetContent>
    </Sheet>
  );
}
